import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMobileStands } from '../services/mobileStandService';
import { getMenuProducts } from '../services/menuProductService';
import { getClientByDocument, getClients, changeStateClient } from '../services/clientService';
import { createInvoice, getInvoicesByStand } from '../services/invoiceService';
import { formatBs, formatCurrency } from '../utils/format';
import { describePortionChanges, portionsKey } from '../utils/portions';
import ItemPorcionesModal from '../components/ItemPorcionesModal';
import { resolveImageUrl } from '../utils/media';
import { paymentMethodIcon, paymentMethodLabel } from '../utils/payments';
import FacturaDetalleModal from '../components/FacturaDetalleModal';
import ClientForm from '../components/ClientForm';
import CobroModal from '../components/CobroModal';

const HELD_KEY = 'origenes_facturas_espera';
const STAND_KEY = 'origenes_facturacion_stand';
const RATE_KEY = 'origenes_tasa_bs';
const NATIONALITIES = ['V', 'E', 'J', 'G'];
const DOCUMENT_REGEX = /^\d{5,10}(-\d)?$/;

const EMPTY_CLIENT = { nationality: 'V', document: '', name: '', phone: '' };

const newLineId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Sin acceso a localStorage: se ignora
  }
}

function TabButton({ label, icon, count, active, onClick }) {
  return (
    <button
      className="btn-orig-ghost"
      onClick={onClick}
      style={{
        padding: '10px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
        color: active ? 'var(--orig-green-bright)' : 'var(--orig-text-muted)',
        borderBottom: active ? '2px solid var(--orig-green)' : '2px solid transparent',
        marginBottom: -1, whiteSpace: 'nowrap',
      }}
    >
      <i className={`bi ${icon}`} style={{ marginRight: 6 }} />
      {label}
      {count > 0 && (
        <span className="badge-orig badge-amber" style={{ marginLeft: 8, padding: '1px 7px' }}>{count}</span>
      )}
    </button>
  );
}

function LoadingRow({ label }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
      <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: 'block', marginBottom: 10, animation: 'spin 1s linear infinite' }} />
      {label}
    </div>
  );
}

function EmptyRow({ icon, title, subtitle }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
      <i className={`bi ${icon}`} style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12 }}>{subtitle}</div>
    </div>
  );
}

function Alert({ type, children }) {
  return (
    <div className={`alert-orig ${type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 14 }}>
      <i className={`bi ${type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`} />
      {children}
    </div>
  );
}

export default function Facturacion() {
  const [activeTab, setActiveTab] = useState('new');

  // Stands
  const [stands, setStands] = useState([]);
  const [selectedStandId, setSelectedStandId] = useState(() => readStorage(STAND_KEY, ''));
  const [exchangeRate, setExchangeRate] = useState(() => readStorage(RATE_KEY, ''));

  // Productos del menú
  const [menuProducts, setMenuProducts] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [menuError, setMenuError] = useState('');
  const [search, setSearch] = useState('');

  // Factura en curso
  const [cart, setCart] = useState([]);
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [clientStatus, setClientStatus] = useState('idle'); // idle | searching | found | new
  const [observations, setObservations] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showCobro, setShowCobro] = useState(false);
  const [cobroError, setCobroError] = useState('');
  const [editingLine, setEditingLine] = useState(null);

  // Clientes
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientForm, setClientForm] = useState(null); // { client?, initial?, forInvoice? }
  const [clientBusy, setClientBusy] = useState(false);

  // Facturas en espera (localStorage)
  const [held, setHeld] = useState(() => readStorage(HELD_KEY, []));

  // Historial
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [historyDate, setHistoryDate] = useState(today);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  useEffect(() => { writeStorage(HELD_KEY, held); }, [held]);
  useEffect(() => { writeStorage(STAND_KEY, selectedStandId); }, [selectedStandId]);
  useEffect(() => { writeStorage(RATE_KEY, exchangeRate); }, [exchangeRate]);

  useEffect(() => {
    getMobileStands()
      .then(data => setStands(Array.isArray(data) ? data.filter(s => s.state) : []))
      .catch(() => setStands([]));

    getMenuProducts()
      .then(data => setMenuProducts(Array.isArray(data) ? data.filter(p => p.state) : []))
      .catch(() => setMenuError('No se pudo cargar el menú.'))
      .finally(() => setLoadingMenu(false));
  }, []);

  const loadInvoices = useCallback(() => {
    if (!selectedStandId) { setInvoices([]); return; }
    setLoadingInvoices(true);
    setInvoicesError('');
    getInvoicesByStand(selectedStandId, historyDate)
      .then(data => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoicesError('No se pudo cargar el historial de facturas.'))
      .finally(() => setLoadingInvoices(false));

      console.log(invoices )
  }, [selectedStandId, historyDate]);

  useEffect(() => {
    if (activeTab === 'history') loadInvoices();
  }, [activeTab, loadInvoices]);

  const loadClients = useCallback((searchText) => {
    setLoadingClients(true);
    setClientsError('');
    return getClients(searchText)
      .then(data => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClientsError('No se pudo cargar el listado de clientes.'))
      .finally(() => setLoadingClients(false));
  }, []);

  // Búsqueda de clientes con un pequeño retardo mientras se escribe
  useEffect(() => {
    if (activeTab !== 'clients') return;
    const t = setTimeout(() => loadClients(clientSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [activeTab, clientSearch, loadClients]);

  const selectedStand = stands.find(s => String(s.id) === String(selectedStandId));

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? menuProducts.filter(p => p.name.toLowerCase().includes(q)) : menuProducts;
  }, [menuProducts, search]);

  const rate = Number(exchangeRate) || 0;
  const total = cart.reduce((sum, i) => sum + (Number(i.price) || 0) * i.quantity, 0);
  const totalBs = Math.round(total * rate * 100) / 100;
  const itemCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const validInvoices = invoices.filter(i => i.state);
  const dayTotal = validInvoices.reduce((sum, i) => sum + (Number(i.totalUsd) || 0), 0);
  const dayTotalBs = validInvoices.reduce((sum, i) => sum + (Number(i.totalBs) || 0), 0);

  function flash(message) {
    setNotice(message);
    setTimeout(() => setNotice(''), 3500);
  }

  // --- Carrito ---
  // Cada línea tiene su propio lineId: el mismo producto puede estar en varias líneas
  // con distinta personalización de porciones (portions = null => receta original).
  function addProduct(product) {
    setError('');
    setCart(prev => {
      const existing = prev.find(i => i.menuProductId === product.id && !i.portions);
      if (existing) {
        return prev.map(i => i.lineId === existing.lineId ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        lineId: newLineId(), menuProductId: product.id, name: product.name,
        price: product.price, image: product.image, quantity: 1, portions: null,
      }];
    });
  }

  function setQuantity(lineId, quantity) {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    setCart(prev => prev.map(i => i.lineId === lineId ? { ...i, quantity: qty } : i));
  }

  function removeItem(lineId) {
    setCart(prev => prev.filter(i => i.lineId !== lineId));
  }

  function applyPortions(lineId, portions) {
    setCart(prev => {
      const line = prev.find(i => i.lineId === lineId);
      // Si ya hay otra línea igual (mismo producto y misma personalización), se unen
      const twin = prev.find(i => i.lineId !== lineId && i.menuProductId === line.menuProductId
        && portionsKey(i.portions) === portionsKey(portions));
      if (twin) {
        return prev
          .filter(i => i.lineId !== lineId)
          .map(i => i.lineId === twin.lineId ? { ...i, quantity: i.quantity + line.quantity } : i);
      }
      return prev.map(i => i.lineId === lineId ? { ...i, portions } : i);
    });
    setEditingLine(null);
  }

  function resetInvoice() {
    setCart([]);
    setClient(EMPTY_CLIENT);
    setClientStatus('idle');
    setObservations('');
    setError('');
  }

  // --- Cliente ---
  function handleClientChange(field, value) {
    if (field === 'document') value = value.replace(/[^\d-]/g, '');
    setClient(prev => ({ ...prev, [field]: value }));
    if (field === 'document' || field === 'nationality') setClientStatus('idle');
  }

  async function searchClient() {
    if (!DOCUMENT_REGEX.test(client.document)) {
      setError('Ingrese una cédula o RIF válido (ej: 12345678 o 12345678-9).');
      return;
    }
    setError('');
    setClientStatus('searching');
    try {
      const found = await getClientByDocument(client.nationality, client.document);
      setClient({ nationality: found.nationality, document: found.document, name: found.name, phone: found.phone || '' });
      setClientStatus('found');
    } catch (err) {
      if (err.response?.status === 404) {
        setClient(prev => ({ ...prev, name: '', phone: '' }));
        setClientStatus('new');
      } else {
        setClientStatus('idle');
        setError('No se pudo buscar el cliente.');
      }
    }
  }

  // --- Facturas en espera ---
  function buildHeld() {
    return {
      id: Date.now(),
      mobileStandId: selectedStandId,
      standName: selectedStand?.name || '',
      client,
      items: cart,
      observations,
      createdAt: new Date().toISOString(),
    };
  }

  function holdInvoice() {
    if (!cart.length) {
      setError('Agregue al menos un producto antes de poner la factura en espera.');
      return;
    }
    setHeld(prev => [buildHeld(), ...prev]);
    resetInvoice();
    flash('Factura puesta en espera.');
  }

  function resumeHeld(item) {
    setHeld(prev => {
      const rest = prev.filter(h => h.id !== item.id);
      // Si hay una factura en curso, se intercambia con la retomada
      return cart.length ? [buildHeld(), ...rest] : rest;
    });
    if (item.mobileStandId) setSelectedStandId(String(item.mobileStandId));
    setCart((item.items || []).map(i => ({ portions: null, ...i, lineId: i.lineId || newLineId() })));
    setClient({ ...EMPTY_CLIENT, ...item.client });
    setClientStatus('idle');
    setObservations(item.observations || '');
    setError('');
    setActiveTab('new');
    if (cart.length) flash('La factura en curso se guardó en espera.');
  }

  function discardHeld(id) {
    setHeld(prev => prev.filter(h => h.id !== id));
  }

  // --- Clientes ---
  function openNewClientForInvoice() {
    setClientForm({
      initial: { nationality: client.nationality, document: client.document, name: client.name, phone: client.phone },
      forInvoice: true,
    });
  }

  function handleClientSaved(saved) {
    const target = clientForm;
    setClientForm(null);
    if (target?.forInvoice) {
      setClient({ nationality: saved.nationality, document: saved.document, name: saved.name, phone: saved.phone || '' });
      setClientStatus('found');
      setError('');
    } else {
      loadClients(clientSearch.trim());
      flash(target?.client ? 'Cliente actualizado.' : 'Cliente registrado.');
    }
  }

  function selectClientForInvoice(c) {
    setClient({ nationality: c.nationality, document: c.document, name: c.name, phone: c.phone || '' });
    setClientStatus('found');
    setError('');
    setActiveTab('new');
  }

  async function toggleClientState(c) {
    setClientBusy(true);
    try {
      await changeStateClient(c.id);
      await loadClients(clientSearch.trim());
    } catch (err) {
      setClientsError(err.response?.data?.msg || 'No se pudo cambiar el estado del cliente.');
    } finally {
      setClientBusy(false);
    }
  }

  // --- Facturar: valida y abre el cobro ---
  function submitInvoice() {
    setError('');
    if (!selectedStandId) return setError('Seleccione el stand móvil.');
    if (!cart.length) return setError('Agregue al menos un producto a la factura.');
    if (!DOCUMENT_REGEX.test(client.document)) return setError('Ingrese una cédula o RIF válido (ej: 12345678 o 12345678-9).');
    if (!client.name.trim()) return setError('Ingrese el nombre o razón social del cliente.');
    if (!(rate > 0)) return setError('Ingrese la tasa del día (Bs por dólar) antes de facturar.');

    setCobroError('');
    setShowCobro(true);
  }

  async function confirmInvoice(payments) {
    setCobroError('');
    setSaving(true);
    try {
      const data = await createInvoice({
        mobileStandId: Number(selectedStandId),
        client: {
          nationality: client.nationality,
          document: client.document,
          name: client.name.trim(),
          phone: client.phone.trim() || null,
        },
        observations: observations.trim() || null,
        exchangeRate: rate,
        details: cart.map(i => ({
          menuProductId: i.menuProductId,
          quantity: i.quantity,
          ...(i.portions ? { portions: i.portions.map(p => ({ portionId: p.portionId, quantity: p.quantity })) } : {}),
        })),
        payments,
      });
      setShowCobro(false);
      resetInvoice();
      setViewingInvoice(data.invoice);
    } catch (err) {
      setCobroError(err.response?.data?.msg || 'No se pudo registrar la factura.');
    } finally {
      setSaving(false);
    }
  }

  function handleCancelled(updated) {
    setViewingInvoice(updated);
    loadInvoices();
  }

  const clientBadge = {
    found: <span className="badge-orig badge-green">Cliente registrado</span>,
    new: <span className="badge-orig badge-amber">Cliente nuevo</span>,
  }[clientStatus];

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
          Ventas del stand móvil
        </p>
        <h5 style={{
          fontWeight: 800, color: 'var(--orig-text)',
          fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
        }}>
          Facturación
        </h5>
      </div>

      {/* Selector de stand */}
      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-body" style={{ padding: '16px 20px' }}>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6">
              <label className="form-label">Stand móvil</label>
              <select
                className="form-select"
                value={selectedStandId}
                onChange={e => setSelectedStandId(e.target.value)}
              >
                <option value="">Seleccione un stand</option>
                {stands.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Tasa del día (Bs por $)</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 12, fontWeight: 700, color: 'var(--orig-text-muted)', pointerEvents: 'none',
                }}>
                  Bs
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="0,00"
                  value={exchangeRate}
                  onChange={e => setExchangeRate(e.target.value)}
                  style={{
                    paddingLeft: 32, marginBottom: 0,
                    borderColor: rate > 0 ? undefined : 'var(--orig-amber)',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--orig-border)', overflowX: 'auto' }}>
        <TabButton label="Nueva Factura" icon="bi-cart3" active={activeTab === 'new'} onClick={() => setActiveTab('new')} />
        <TabButton label="En Espera" icon="bi-pause-circle" count={held.length} active={activeTab === 'held'} onClick={() => setActiveTab('held')} />
        <TabButton label="Historial" icon="bi-clock-history" active={activeTab === 'history'} onClick={() => setActiveTab('history')} />
        <TabButton label="Clientes" icon="bi-people" active={activeTab === 'clients'} onClick={() => setActiveTab('clients')} />
      </div>

      {notice && <Alert type="success">{notice}</Alert>}

      {/* Tab: Nueva factura */}
      {activeTab === 'new' && (
        !selectedStandId ? (
          <div className="section-card">
            <EmptyRow icon="bi-shop-window" title="Seleccione un stand" subtitle="Elija el stand móvil desde el que va a facturar." />
          </div>
        ) : (
          <div className="row g-4">
            {/* Productos */}
            <div className="col-12 col-lg-7">
              <div className="section-card">
                <div className="section-header">
                  <h6>
                    <i className="bi bi-egg-fried" style={{ color: 'var(--orig-green)' }} />
                    Menú
                  </h6>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar producto..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ maxWidth: 220, marginBottom: 0 }}
                  />
                </div>

                {loadingMenu ? (
                  <LoadingRow label="Cargando menú..." />
                ) : menuError ? (
                  <div style={{ padding: 20 }}><Alert>{menuError}</Alert></div>
                ) : filteredProducts.length === 0 ? (
                  <EmptyRow icon="bi-egg-fried" title="Sin productos" subtitle="No hay productos activos en el menú." />
                ) : (
                  <div className="section-body">
                    <div className="fact-grid">
                      {filteredProducts.map(p => {
                        const inCartQty = cart
                          .filter(i => i.menuProductId === p.id)
                          .reduce((s, i) => s + i.quantity, 0);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={`fact-product ${inCartQty ? 'selected' : ''}`}
                            onClick={() => addProduct(p)}
                            title={`Agregar ${p.name}`}
                          >
                            {inCartQty > 0 && <span className="fact-qty">{inCartQty}</span>}
                            <div className="fact-img">
                              {p.image ? (
                                <img src={resolveImageUrl(p.image)} alt={p.name} />
                              ) : (
                                <i className="bi bi-image" style={{ fontSize: 22, color: 'var(--orig-text-hint)' }} />
                              )}
                            </div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--orig-text)', lineHeight: 1.25 }}>{p.name}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-green-bright)' }}>{formatCurrency(p.price)}</div>
                            {rate > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginTop: -4 }}>{formatBs((Number(p.price) || 0) * rate)}</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Factura */}
            <div className="col-12 col-lg-5">
              <div className="section-card" style={{ position: 'sticky', top: 16 }}>
                <div className="section-header">
                  <h6>
                    <i className="bi bi-receipt" style={{ color: 'var(--orig-green)' }} />
                    Factura — {selectedStand?.name}
                  </h6>
                  {cart.length > 0 && (
                    <button
                      type="button"
                      className="btn-orig btn-orig-outline"
                      onClick={resetInvoice}
                      style={{ padding: '4px 10px', fontSize: 12 }}
                      title="Limpiar factura"
                    >
                      <i className="bi bi-eraser" />
                      Limpiar
                    </button>
                  )}
                </div>

                <div className="section-body">
                  {error && <Alert>{error}</Alert>}

                  {/* Cliente */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text)' }}>Cliente</span>
                    {clientBadge}
                  </div>
                  <div className="row g-2" style={{ marginBottom: 16 }}>
                    <div className="col-12">
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select
                          className="form-select"
                          value={client.nationality}
                          onChange={e => handleClientChange('nationality', e.target.value)}
                          style={{ width: 70, flexShrink: 0 }}
                          title="Nacionalidad"
                        >
                          {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Cédula o RIF (ej: 12345678-9)"
                          value={client.document}
                          onChange={e => handleClientChange('document', e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchClient(); } }}
                          style={{ marginBottom: 0 }}
                        />
                        <button
                          type="button"
                          className="btn-orig btn-orig-outline"
                          onClick={searchClient}
                          disabled={clientStatus === 'searching'}
                          title="Buscar cliente"
                          style={{ flexShrink: 0 }}
                        >
                          <i className={`bi ${clientStatus === 'searching' ? 'bi-arrow-repeat' : 'bi-search'}`} />
                        </button>
                        <button
                          type="button"
                          className="btn-orig btn-orig-green"
                          onClick={openNewClientForInvoice}
                          title="Registrar nuevo cliente"
                          style={{ flexShrink: 0 }}
                        >
                          <i className="bi bi-person-plus" />
                        </button>
                      </div>
                      {clientStatus === 'new' && (
                        <div style={{ fontSize: 11.5, color: 'var(--orig-amber-light)', marginTop: 6 }}>
                          <i className="bi bi-info-circle" style={{ marginRight: 4 }} />
                          No está registrado. Complete el nombre abajo (se registrará al facturar) o use
                          <button
                            type="button"
                            onClick={openNewClientForInvoice}
                            style={{ background: 'none', border: 'none', padding: '0 4px', color: 'var(--orig-green-bright)', fontWeight: 600, cursor: 'pointer', fontSize: 11.5 }}
                          >
                            Registrar cliente
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-sm-7">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nombre o razón social"
                        value={client.name}
                        onChange={e => handleClientChange('name', e.target.value)}
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                    <div className="col-12 col-sm-5">
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Teléfono (opcional)"
                        value={client.phone}
                        onChange={e => handleClientChange('phone', e.target.value)}
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  </div>

                  {/* Productos de la factura */}
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text)', marginBottom: 8 }}>
                    Productos {itemCount > 0 && <span style={{ fontWeight: 500, color: 'var(--orig-text-muted)' }}>({itemCount})</span>}
                  </div>

                  {cart.length === 0 ? (
                    <div style={{
                      padding: '22px 12px', textAlign: 'center', fontSize: 12.5, color: 'var(--orig-text-muted)',
                      border: '1px dashed var(--orig-border)', borderRadius: 'var(--orig-radius)', marginBottom: 16,
                    }}>
                      <i className="bi bi-hand-index" style={{ marginRight: 6 }} />
                      Toque un producto del menú para agregarlo
                    </div>
                  ) : (
                    <div style={{ marginBottom: 16 }}>
                      {cart.map(item => {
                        const changes = describePortionChanges(item.portions);
                        return (
                        <div key={item.lineId} className="fact-line">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.name}
                            </div>
                            <div style={{ fontSize: 11.5, color: 'var(--orig-text-muted)' }}>{formatCurrency(item.price)} c/u</div>
                            {changes.length > 0 && (
                              <div style={{ fontSize: 11, lineHeight: 1.35, marginTop: 2 }}>
                                {changes.map((c, idx) => (
                                  <span
                                    key={idx}
                                    style={{ color: c.type === 'extra' ? 'var(--orig-green-bright)' : 'var(--orig-amber-light)', marginRight: 8 }}
                                  >
                                    {c.text}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="fact-step"
                            onClick={() => setEditingLine(item)}
                            title="Agregar o quitar porciones"
                            style={item.portions ? { borderColor: 'var(--orig-green)', color: 'var(--orig-green-bright)' } : undefined}
                          >
                            <i className="bi bi-sliders" />
                          </button>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <button
                              type="button"
                              className="fact-step"
                              onClick={() => item.quantity > 1 ? setQuantity(item.lineId, item.quantity - 1) : removeItem(item.lineId)}
                              title={item.quantity > 1 ? 'Restar' : 'Quitar'}
                            >
                              <i className={`bi ${item.quantity > 1 ? 'bi-dash' : 'bi-trash'}`} />
                            </button>
                            <input
                              type="number"
                              min="1"
                              className="form-control"
                              value={item.quantity}
                              onChange={e => setQuantity(item.lineId, e.target.value)}
                              style={{ width: 52, textAlign: 'center', padding: '4px 2px', marginBottom: 0 }}
                            />
                            <button
                              type="button"
                              className="fact-step"
                              onClick={() => setQuantity(item.lineId, item.quantity + 1)}
                              title="Sumar"
                            >
                              <i className="bi bi-plus" />
                            </button>
                          </div>
                          <div style={{ width: 76, textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--orig-text)' }}>
                            {formatCurrency((Number(item.price) || 0) * item.quantity)}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Observaciones (opcional)"
                    value={observations}
                    onChange={e => setObservations(e.target.value)}
                    style={{ marginBottom: 16 }}
                  />

                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    paddingTop: 12, borderTop: '1px solid var(--orig-border)',
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text-muted)' }}>TOTAL</span>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--orig-green-bright)', fontFamily: 'var(--orig-font-display)', lineHeight: 1.1 }}>
                        {formatCurrency(total)}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: rate > 0 ? 'var(--orig-text)' : 'var(--orig-amber-light)' }}>
                        {rate > 0 ? formatBs(totalBs) : 'Ingrese la tasa del día'}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex', gap: 10,
                  padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
                }}>
                  <button
                    type="button"
                    className="btn-orig btn-orig-outline"
                    onClick={holdInvoice}
                    disabled={saving || !cart.length}
                    style={{ flex: 1, justifyContent: 'center' }}
                    title="Guardar la factura para continuarla después"
                  >
                    <i className="bi bi-pause-circle" />
                    En espera
                  </button>
                  <button
                    type="button"
                    className="btn-orig btn-orig-green"
                    onClick={submitInvoice}
                    disabled={saving || !cart.length}
                    style={{ flex: 2, justifyContent: 'center' }}
                  >
                    <i className="bi bi-check2-circle" />
                    {saving ? 'Facturando...' : 'Facturar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Tab: En espera */}
      {activeTab === 'held' && (
        <div className="section-card">
          <div className="section-header">
            <h6>
              <i className="bi bi-pause-circle" style={{ color: 'var(--orig-green)' }} />
              Facturas en espera
            </h6>
            <span className="badge-orig badge-amber">
              {held.length} {held.length === 1 ? 'factura' : 'facturas'}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {held.length === 0 ? (
              <EmptyRow icon="bi-pause-circle" title="Sin facturas en espera" subtitle="Use el botón «En espera» para guardar una factura y retomarla después." />
            ) : (
              <table className="orig-table">
                <thead>
                  <tr>
                    <th>Hora</th>
                    <th>Cliente</th>
                    <th>Stand</th>
                    <th>Productos</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {held.map(h => {
                    const hTotal = (h.items || []).reduce((s, i) => s + (Number(i.price) || 0) * i.quantity, 0);
                    const hCount = (h.items || []).reduce((s, i) => s + i.quantity, 0);
                    return (
                      <tr key={h.id}>
                        <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                          {new Date(h.createdAt).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
                          <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>{new Date(h.createdAt).toLocaleDateString('es-VE')}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{h.client?.name || 'Sin nombre'}</div>
                          <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                            {h.client?.document ? `${h.client.nationality}-${h.client.document}` : 'Sin documento'}
                          </div>
                        </td>
                        <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{h.standName || '—'}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{hCount} {hCount === 1 ? 'producto' : 'productos'}</div>
                          <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {(h.items || []).map(i => `${i.quantity}× ${i.name}`).join(', ')}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(hTotal)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn-orig btn-orig-green"
                              onClick={() => resumeHeld(h)}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Retomar factura"
                            >
                              <i className="bi bi-play-circle" />
                              Retomar
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => discardHeld(h.id)}
                              style={{ padding: '5px 12px', fontSize: 12, color: 'var(--orig-red-light)' }}
                              title="Descartar factura en espera"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Tab: Historial */}
      {activeTab === 'history' && (
        !selectedStandId ? (
          <div className="section-card">
            <EmptyRow icon="bi-shop-window" title="Seleccione un stand" subtitle="Elija un stand móvil para ver sus facturas." />
          </div>
        ) : (
          <div className="section-card">
            <div className="section-header" style={{ flexWrap: 'wrap', gap: 10 }}>
              <h6>
                <i className="bi bi-clock-history" style={{ color: 'var(--orig-green)' }} />
                Facturas — {selectedStand?.name}
              </h6>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* 
                {!loadingInvoices && (
                  
                  <span className="badge-orig badge-green">
                    {validInvoices.length} · {formatCurrency(dayTotal)} · {formatBs(dayTotalBs)}
                  </span>
                  
                )}
                  */}
                <input
                  type="date"
                  className="form-control"
                  value={historyDate}
                  onChange={e => setHistoryDate(e.target.value)}
                  style={{ width: 160, marginBottom: 0 }}
                />
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              {loadingInvoices ? (
                <LoadingRow label="Cargando facturas..." />
              ) : invoicesError ? (
                <div style={{ padding: 20 }}><Alert>{invoicesError}</Alert></div>
              ) : invoices.length === 0 ? (
                <EmptyRow icon="bi-receipt" title="Sin facturas" subtitle="No hay facturas registradas en esta fecha." />
              ) : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>Hora</th>
                      <th>Cliente</th>
                      <th>Productos</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th>Pago</th>
                      <th>Estado</th>
                      <th>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => {
                      const count = (inv.details || []).reduce((s, d) => s + d.quantity, 0);
                      return (
                        <tr key={inv.id} style={{ opacity: inv.state ? 1 : 0.6 }}>
                          <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                            {String(inv.id).padStart(6, '0')}
                          </td>
                          <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                            {new Date(inv.date).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td>
                            <div style={{ fontWeight: 500 }}>{inv.client?.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                              {inv.client ? `${inv.client.nationality}-${inv.client.document}` : ''}
                            </div>
                          </td>
                          <td style={{ fontSize: 12.5 }}>{count} {count === 1 ? 'producto' : 'productos'}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {(inv.payments || []).map((p, index) => (
                              

                              <>
                                {p.amountUsd > 0 && (
                                  <div style={{ fontWeight: 700 }}>{formatCurrency(p.amountUsd)}</div>
                                )}
                                {p.amountBs > 0 && (
                                  <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>{formatBs(p.amountBs)}</div>
                                )}
                              </>
                            ))}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {(inv.payments || []).map(p => (
                              <i
                                key={p.id}
                                className={`bi ${paymentMethodIcon(p.method)}`}
                                title={`${paymentMethodLabel(p.method)}: ${Number(p.amountUsd) > 0 ? formatCurrency(p.amountUsd) : formatBs(p.amountBs)}`}
                                style={{ fontSize: 16, color: 'var(--orig-green)', marginRight: 6 }}
                              />
                            ))}
                          </td>
                          <td>
                            <span className={`badge-orig ${inv.state ? 'badge-green' : 'badge-red'}`}>
                              <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                              {inv.state ? 'Emitida' : 'Anulada'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => setViewingInvoice(inv)}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Ver detalle de la factura"
                            >
                              <i className="bi bi-eye" />
                              Ver
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )
      )}

      {/* Tab: Clientes */}
      {activeTab === 'clients' && (
        <>
          <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre o cédula/RIF..."
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              style={{ maxWidth: 320, marginBottom: 0 }}
            />
            <button className="btn-orig btn-orig-green" onClick={() => setClientForm({})}>
              <i className="bi bi-person-plus" />
              Nuevo Cliente
            </button>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h6>
                <i className="bi bi-people" style={{ color: 'var(--orig-green)' }} />
                Clientes
              </h6>
              {!loadingClients && (
                <span className="badge-orig badge-green">
                  {clients.length} {clients.length === 1 ? 'cliente' : 'clientes'}
                </span>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              {loadingClients ? (
                <LoadingRow label="Cargando clientes..." />
              ) : clientsError ? (
                <div style={{ padding: 20 }}><Alert>{clientsError}</Alert></div>
              ) : clients.length === 0 ? (
                <EmptyRow icon="bi-people" title="Sin resultados" subtitle="No hay clientes registrados con ese criterio." />
              ) : (
                <table className="orig-table">
                  <thead>
                    <tr>
                      <th>Cédula / RIF</th>
                      <th>Nombre o razón social</th>
                      <th>Teléfono</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map(c => (
                      <tr key={c.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12.5, whiteSpace: 'nowrap' }}>{c.nationality}-{c.document}</td>
                        <td style={{ fontWeight: 500 }}>{c.name}</td>
                        <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{c.phone || '—'}</td>
                        <td>
                          <span className={`badge-orig ${c.state ? 'badge-green' : 'badge-red'}`}>
                            <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
                            {c.state ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn-orig btn-orig-green"
                              onClick={() => selectClientForInvoice(c)}
                              disabled={!c.state}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Usar en la factura en curso"
                            >
                              <i className="bi bi-receipt" />
                              Facturar
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => setClientForm({ client: c })}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Editar cliente"
                            >
                              <i className="bi bi-pencil" />
                              Editar
                            </button>
                            <button
                              className="btn-orig btn-orig-outline"
                              onClick={() => toggleClientState(c)}
                              disabled={clientBusy}
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              title="Cambiar estado"
                            >
                              <i className="bi bi-toggle2-on" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal: crear/editar cliente */}
      {clientForm && (
        <ClientForm
          client={clientForm.client}
          initial={clientForm.initial}
          onCancel={() => setClientForm(null)}
          onSaved={handleClientSaved}
        />
      )}

      {/* Modal: porciones adicionales / quitar porciones de una línea */}
      {editingLine && (
        <ItemPorcionesModal
          item={editingLine}
          onClose={() => setEditingLine(null)}
          onSave={portions => applyPortions(editingLine.lineId, portions)}
        />
      )}

      {/* Modal: cobro con una o varias formas de pago */}
      {showCobro && (
        <CobroModal
          totalUsd={Math.round(total * 100) / 100}
          exchangeRate={rate}
          client={client}
          saving={saving}
          error={cobroError}
          onCancel={() => setShowCobro(false)}
          onConfirm={confirmInvoice}
        />
      )}

      {/* Modal: detalle de factura */}
      {viewingInvoice && (
        <FacturaDetalleModal
          key={viewingInvoice.id}
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          onCancelled={handleCancelled}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .fact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
          gap: 12px;
        }
        .fact-product {
          position: relative;
          display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
          padding: 10px; text-align: left; cursor: pointer;
          background: var(--orig-panel);
          border: 1px solid var(--orig-border);
          border-radius: var(--orig-radius);
          transition: border-color 0.15s, transform 0.1s;
        }
        .fact-product:hover { border-color: var(--orig-green); }
        .fact-product:active { transform: scale(0.97); }
        .fact-product.selected { border-color: var(--orig-green); background: var(--orig-green-dim); }
        .fact-img {
          width: 100%; aspect-ratio: 4 / 3; border-radius: 6px; overflow: hidden;
          background: var(--orig-card); border: 1px solid var(--orig-border);
          display: flex; align-items: center; justify-content: center;
        }
        .fact-img img { width: 100%; height: 100%; object-fit: cover; }
        .fact-qty {
          position: absolute; top: 6px; right: 6px; z-index: 1;
          min-width: 24px; height: 24px; padding: 0 6px; border-radius: 12px;
          background: var(--orig-green); color: #fff; font-size: 12px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }
        .fact-line {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 0; border-bottom: 1px solid var(--orig-border);
        }
        .fact-step {
          width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
          background: var(--orig-panel); border: 1px solid var(--orig-border);
          color: var(--orig-text); display: flex; align-items: center; justify-content: center;
        }
        .fact-step:hover { border-color: var(--orig-green); }
      `}</style>
    </div>
  );
}
